import type { StoryContentType } from '~/types/story'

export type UsePageMetaProps = {
  blok: Ref<StoryContentType | undefined>
  seoImageUrl?: Ref<string | undefined>
}

export default function usePageMeta({
  blok,
  seoImageUrl = ref(),
}: UsePageMetaProps) {
  const {
    public: { algoliaVerification },
  } = useRuntimeConfig()

  const route = useRoute()
  const site = useSiteConfig()
  const storyContent = useStoryContent({ blok })
  const { firstPublishedAt, displayDate } = usePage()

  // Helper functions to safely handle arrays and strings
  const safeArrayString = (value: unknown) => {
    if (!value) return ''
    if (!Array.isArray(value)) return String(value)
    return value.filter(Boolean).join(',')
  }
  const safeString = (value: unknown) => {
    console.log('safeString', value)
    if (value === null || value === undefined) return ''
    return String(value).trim()
  }

  const seoData = computed(() => blok.value?.seo)

  const title = computed((): string =>
    safeString(
      storyContent?.pageBanner.value?.title?.value ||
        storyContent?.title.value ||
        ogTitle.value ||
        '',
    ),
  )

  const ogTitle = computed((): string =>
    safeString(
      seoData.value?.og_title ||
        seoData.value?.title ||
        storyContent?.pageBanner.value?.title?.value ||
        storyContent?.title.value ||
        site?.name ||
        '',
    ),
  )

  const description = computed((): string =>
    safeString(
      seoData.value?.og_description ||
        storyContent?.introduction.value ||
        site?.description ||
        '',
    ),
  )

  const image = computed((): string =>
    safeString(
      seoImageUrl?.value ||
        storyContent?.seoGraphicMedia.value ||
        // @ts-expect-error - TODO: fix this - these are sometimes coming through with an extra level of nesting for some reason on Service Providers?
        storyContent?.media?.value?.filename?.filename ||
        storyContent?.media?.value?.filename ||
        site?.image ||
        '',
    ),
  )

  const hasCustomOgImage = computed(() => !!seoData.value?.og_image)
  const useGraphicVersion = computed(
    () =>
      !!storyContent?.seoGraphicMedia?.value?.length ||
      storyContent.showTitleOnOgImage.value,
  )

  const safeMeta = computed(() => {
    try {
      return JSON.stringify(storyContent?.meta.value)
    } catch (err) {
      console.warn('Error parsing safe meta', err)
      return '{}'
    }
  })

  // split search_helper into an array on newlines, remove duplicates and empty strings, then join with commas
  const searchHelper = computed((): string =>
    safeArrayString(
      blok.value?.search_helper
        ?.split('\n')
        .filter(
          (item: string, index: number, self: string[]) =>
            item !== '' && self.indexOf(item) === index,
        ),
    ),
  )

  const searchIndexMetaSchema = computed(() =>
    Object.entries({
      title,
      description,
      meta: safeMeta.value,
      searchHelper: searchHelper.value,
      fullSlug: route.fullPath,
      audiences: safeArrayString(blok.value?.audiences),
      bannerImage: safeString(blok.value?.intro?.[0]?.image?.filename),
      component: safeString(blok.value?.component),
      displayDate: safeString(displayDate.value),
      firstPublishedAt: safeString(firstPublishedAt.value),
      icon: safeString(blok.value?.icon),
      image: safeString(blok.value?.image?.filename || image.value),
      logo: safeString(blok.value?.logo?.filename),
      organisation: safeString(blok.value?.organisation),
      organisationLink: safeString(blok.value?.link?.url),
      serviceType: safeArrayString(blok.value?.service_type),
      states: safeArrayString(blok.value?.states),
      topics: safeArrayString(blok.value?.topics),
      type: safeString(blok.value?.type),
      eaaaMember: blok.value?.eaaa_member ? 'yes' : null,
    })
      .map(([key, value]) => ({
        name: `compass:${kebabCase(key)}`,
        content: unref(value),
      }))
      .filter((item) => !!item.content),
  )

  // Set search index meta schema in page head - this is used by Algolia to index the page properties
  useHead({
    meta: [
      ...searchIndexMetaSchema.value,
      {
        name: 'algolia-site-verification',
        content: algoliaVerification?.code || '',
      },
    ],
  })

  // Set page meta - adds standard meta tags to the page head
  useSeoMeta({
    title,
    ogTitle,
    description,
    ogDescription: description,
    twitterCard: 'summary_large_image',
  })

  // Define og image component - adds the custom generated og image to the page head
  defineOgImageComponent('Basic', {
    image: hasCustomOgImage.value ? seoData.value?.og_image : image.value,
    imageFocalPoint: storyContent?.mediaFocalPoint?.value,
    title: ogTitle.value,
    description: description,
    useCustomImage: hasCustomOgImage.value,
    useGraphicImage: !hasCustomOgImage.value && useGraphicVersion.value,
    tag: storyContent?.resourceType || null,
  })
}
